import React, {
    useCallback, useRef, useState,
} from 'react'

type Props = {
    initialWidthPercent: number
    minWidthPercent: number
    maxWidthPercent: number
    onWidthChange: (width: number) => void
}
const useResize = ({
    initialWidthPercent,
    minWidthPercent,
    maxWidthPercent,
    onWidthChange,
}: Readonly<Props>) => {
    const containerRef = useRef<HTMLDivElement>(null)

    const [
        isResizing,
        setIsResizing,
    ] = useState<boolean>(false)
    const [
        panelWidth,
        setPanelWidth,
    ] = useState<number>(initialWidthPercent)

    const handleResize = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        if ((e.target as HTMLElement).closest('button')) return

        const startX = e.clientX

        const container = containerRef.current?.parentElement

        if (!container) return

        setIsResizing(true)
        document.body.style.userSelect = 'none'
        document.body.style.pointerEvents = 'none'
        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaX = moveEvent.clientX - startX
            const containerWidth = container.offsetWidth

            const deltaPercent = (deltaX / containerWidth) * 100
            const newWidth = Math.max(
                minWidthPercent,
                Math.min(maxWidthPercent, panelWidth + deltaPercent),
            )

            setPanelWidth(newWidth)
            onWidthChange?.(newWidth)
        }

        const handleMouseUp = () => {
            setIsResizing(false)
            document.body.style.userSelect = ''
            document.body.style.pointerEvents = ''
            document.removeEventListener('mousemove', handleMouseMove)
            document.removeEventListener('mouseup', handleMouseUp)
        }

        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
    }, [
        panelWidth,
        minWidthPercent,
        maxWidthPercent,
        onWidthChange,
    ])

    return {
        isResizing,
        panelWidth,
        handleResize,
        containerRef,
    }
}

export default useResize
